'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import Image from 'next/image';

import type { TDynamicData } from '@eliadmin/core/src/api/dynamics/dynamics-api';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Heading } from '../ui/heading';
import { Paragraph } from '../ui/paragraph';
import { useBreakpoint } from '@eliadmin/core/src/hooks/use-breakpoint';
import type { SectionProps } from './custom-homepage';

export function WhyUs({
  items,
  title,
  subtitle,
  description,
  image,
}: { items: TDynamicData[] } & Partial<SectionProps>) {
  const isDesktop = useBreakpoint('sm');

  return (
    <section className={cn('custom-section', 'py-8 sm:py-10 lg:py-12')}>
      <div
        className={cn(
          'custom-container--xxl',
          'flex flex-col items-start justify-start gap-8 lg:gap-6 xl:flex-row xl:items-stretch xl:justify-between',
        )}
      >
        <div className="flex w-full flex-col items-start justify-between gap-6 xl:max-w-[1059px]">
          <Heading title={title ?? ''} subtitle={subtitle} />
          <div className="grid w-full grid-cols-1 gap-2 sm:grid-cols-3 md:gap-6">
            {items.map(item => (
              <div
                key={item.name}
                className="flex w-full flex-row items-start justify-start gap-4 rounded-[20px] bg-primary-foreground p-6 sm:flex-col sm:gap-12 sm:p-8"
              >
                <div className="flex max-h-14 min-h-14 min-w-14 max-w-14 items-center justify-center rounded-[15px] bg-primary/10 sm:max-h-[72px] sm:min-h-[72px] sm:min-w-[72px] sm:max-w-[72px] sm:rounded-[20px]">
                  <Image
                    src={item?.image?.url ?? '/images/placeholder.png'}
                    alt={resolveImageAlt(item?.image, item?.name)}
                    width={28}
                    height={28}
                    className="object-contain object-center"
                  />
                </div>
                <div className="flex w-full flex-col items-start justify-start gap-3">
                  <Heading title={item.name} tag={isDesktop ? 'h3' : 'h4'} as="h3" />
                  <Paragraph size={isDesktop ? 'lg' : 'base'} className="w-full">
                    {item?.fields[1]?.value}
                  </Paragraph>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative flex h-[300px] w-full flex-col items-end justify-end overflow-hidden rounded-[20px] p-6 sm:h-[420px] md:h-[500px] xl:h-[700px] xl:max-w-[382px]">
          <div className="relative z-10 flex w-full flex-col items-start justify-start gap-4">
            <div className="flex">
              {Array.from({ length: 4 }).map((_, i) => (
                <Image
                  key={i}
                  src={`/images/avatars/${i + 1}.png`}
                  alt={i.toString()}
                  width={44}
                  height={44}
                  className={cn('object-cover object-center', {
                    '-ml-[14px]': i !== 0,
                  })}
                />
              ))}
            </div>
            <Paragraph
              innerHtml={description}
              className="w-full max-w-[216px]"
              size="base"
              color="white"
            />
          </div>
          <div className="primary-overlay" />
          <Image
            src={image ?? '/images/placeholder.png'}
            alt={subtitle ?? ''}
            fill
            className="absolute inset-0 z-0 h-full w-full object-cover object-center grayscale"
          />
        </div>
      </div>
    </section>
  );
}
